/**
 * Constants and configuration objects for ScannerConfigurationForm
 */

// ============================================================================
// Type Definitions (JSDoc for TypeScript-like safety in JavaScript)
// ============================================================================

/**
 * @typedef {'amazon-bedrock' | 'azure-openai' | 'vertex-ai'} TargetSystem
 * Valid target system identifiers
 */

/**
 * @typedef {'access-key' | 'oauth' | 'service-account'} AuthMethod
 * Valid authentication method identifiers
 */

/**
 * @typedef {`${TargetSystem}:${AuthMethod}`} ConnectionScenarioKey
 * Composite key for target system + auth method combination
 */

// ============================================================================
// Target System & Authentication Compatibility
// ============================================================================

/**
 * Compatibility mapping between target systems and their supported authentication methods
 * @type {Record<TargetSystem, AuthMethod[]>}
 */
export const TARGET_SYSTEM_AUTH_COMPATIBILITY = {
  'amazon-bedrock': ['access-key'],
  'azure-openai': ['oauth'],
  'vertex-ai': ['service-account']
};

/**
 * Unique scenario IDs for each valid Target System + Auth Method combination.
 * These IDs are required by the backend when creating scanner configurations.
 *
 * Key format: `${targetSystem}:${authMethod}`
 *
 * @type {Record<string, string>}
 */
export const CONNECTION_SCENARIO_IDS = {
  // Amazon Bedrock scenarios
  'amazon-bedrock:access-key': 'id-bedrock-access-key',

  // Azure OpenAI scenarios
  'azure-openai:oauth': 'id-azure-openai-oauth',

  // Google Vertex AI scenarios
  'vertex-ai:service-account': 'id-vertex-ai-service-account'
};

/**
 * Generates the composite key for a target system + auth method combination.
 *
 * @param {TargetSystem} targetSystem - The target system identifier
 * @param {AuthMethod} authMethod - The authentication method identifier
 * @returns {string} The composite key in format `targetSystem:authMethod`
 */
export const buildConnectionScenarioKey = (targetSystem, authMethod) =>
  `${targetSystem}:${authMethod}`;

/**
 * Retrieves the unique scenario ID for a given Target System + Auth Method combination.
 *
 * @param {TargetSystem} targetSystem - The target system identifier
 * @param {AuthMethod} authMethod - The authentication method identifier
 * @returns {string | null} The scenario ID, or null if the combination is invalid
 * @throws {Error} If the combination is not found and strict mode is enabled
 *
 * @example
 * // Returns 'id-bedrock-access-key'
 * getConnectionScenarioId('amazon-bedrock', 'access-key');
 *
 * @example
 * // Returns null (invalid combination)
 * getConnectionScenarioId('azure-openai', 'access-key');
 */
export const getConnectionScenarioId = (targetSystem, authMethod) => {
  // Validate that the combination is valid according to compatibility rules
  const compatibleMethods = TARGET_SYSTEM_AUTH_COMPATIBILITY[targetSystem];

  if (!compatibleMethods || !compatibleMethods.includes(authMethod)) {
    // Invalid combination - not allowed by compatibility rules
    return null;
  }

  const key = buildConnectionScenarioKey(targetSystem, authMethod);

  return CONNECTION_SCENARIO_IDS[key] || null;
};

/**
 * Checks if a Target System + Auth Method combination is valid.
 *
 * @param {string} targetSystem - The target system identifier
 * @param {string} authMethod - The authentication method identifier
 * @returns {boolean} True if the combination is valid, false otherwise
 */
export const isValidConnectionScenario = (targetSystem, authMethod) => {
  const compatibleMethods = TARGET_SYSTEM_AUTH_COMPATIBILITY[targetSystem];

  return Boolean(compatibleMethods && compatibleMethods.includes(authMethod));
};

// ============================================================================
// Authentication Method Field Configuration
// ============================================================================

/**
 * Configuration for authentication method fields
 */
export const AUTH_METHOD_FIELDS = {
  'access-key': ['accessKeyId', 'secretAccessKey', 'awsRegion'],
  oauth: [
    'clientId',
    'clientSecret',
    'authorizationServerURL',
    'tokenEndpointURL',
    'redirectURI',
    'scope'
  ],
  'service-account': [
    'serviceAccountEmail',
    'projectId',
    'privateKey',
    'location'
  ]
};

/**
 * Auth method field configurations with default values.
 * Each key maps to the fields required for that authentication type.
 */
export const AUTH_FIELD_DEFAULTS = {
  'access-key': {
    accessKeyId: '',
    secretAccessKey: '',
    awsRegion: ''
  },
  oauth: {
    clientId: '',
    clientSecret: '',
    authorizationServerURL: '',
    tokenEndpointURL: '',
    redirectURI: '',
    scope: ''
  },
  'service-account': {
    serviceAccountEmail: '',
    projectId: '',
    privateKey: '',
    location: ''
  }
};

/**
 * Base connection fields that are common across all authentication methods
 */
export const BASE_CONNECTION_FIELDS = ['targetSystem', 'authMethod'];

/**
 * Mapping of sensitive (secret) fields by authentication method
 * These fields require special handling in edit mode:
 * - Never displayed or prefilled when loading edit view
 * - Shown as empty with placeholder indicating secret exists
 * - Send null if unchanged, send new value if modified
 * @type {Record<AuthMethod, string[]>}
 */
export const SENSITIVE_FIELDS_BY_AUTH_METHOD = {
  'access-key': ['secretAccessKey'],
  oauth: ['clientSecret'],
  'service-account': ['privateKey']
};

/**
 * Target system options for the select dropdown
 */
export const TARGET_SYSTEM_OPTIONS = [
  { label: 'Amazon Bedrock', value: 'amazon-bedrock', disabled: false },
  { label: 'Microsoft Azure Copilot', value: 'azure-openai', disabled: false },
  { label: 'Google Vertex AI', value: 'vertex-ai', disabled: false }
];

/**
 * All available authentication methods
 */
export const ALL_AUTH_METHODS = [
  { label: 'Access Keys', value: 'access-key' },
  { label: 'OAuth', value: 'oauth' },
  { label: 'Service Account', value: 'service-account' }
];

/**
 * Run time options (24-hour format, 0-23)
 */
export const RUN_TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  label: `${i}`,
  value: `${i}`
}));

/**
 * Interval unit options for custom schedule
 */
export const INTERVAL_UNIT_OPTIONS = [
  { label: 'days', value: 'days' },
  { label: 'weeks', value: 'weeks' },
  { label: 'months', value: 'months' }
];

/**
 * Week days for custom schedule
 */
export const WEEK_DAYS = [
  { label: 'M', value: 'M' },
  { label: 'T', value: 'T' },
  { label: 'W', value: 'W' },
  { label: 'Th', value: 'Th' },
  { label: 'F', value: 'F' },
  { label: 'S', value: 'S' },
  { label: 'Su', value: 'Su' }
];

/**
 * Day name mapping for schedule labels
 */
export const DAY_NAME_MAP = {
  M: 'Monday',
  T: 'Tuesday',
  W: 'Wednesday',
  Th: 'Thursday',
  F: 'Friday',
  S: 'Saturday',
  Su: 'Sunday'
};

/**
 * Default form values
 */
export const DEFAULT_FORM_VALUES = {
  runFrequency: { unit: 'day', interval: 1 },
  at: '2',
  syncReview: 'auto-resolve',
  emailNotificationsEnabled: false,
  recipientEmails: []
};

/**
 * Default custom schedule state
 */
export const DEFAULT_CUSTOM_SCHEDULE = {
  intervalNumber: 3,
  intervalUnit: 'days',
  repeatDays: [],
  monthDay: 1
};
